/* global Excel console */

import { mockSalesData } from '../shared/mockData';

export async function insertText(text: string) {
  // Write text to the top left cell.
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const range = sheet.getRange("A1");
      range.values = [[text]];
      range.format.autofitColumns();
      await context.sync();
    });
  } catch (error) {
    console.log("Error: " + error);
  }
}

/**
 * Creates a mini bar chart using Unicode characters that fits in a single cell
 * @param values Array of numeric values to visualize
 * @returns String representation of the sparkline
 */
export function createInlineSparkline(values: number[]): string {
  if (!values || values.length === 0) return "";
  
  // Unicode block characters for creating bars
  const bars = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█'];
  
  // Find min and max for scaling
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  // Map values to bar characters
  const sparkline = values.map(value => {
    const normalized = (value - min) / range;
    const index = Math.floor(normalized * (bars.length - 1));
    return bars[Math.max(0, Math.min(index, bars.length - 1))];
  }).join('');
  
  return sparkline;
}

/**
 * Creates a trend indicator with up/down arrows
 * @param values Array of numeric values to analyze
 * @returns String representation of trend
 */
function createTrendIndicator(values: number[]): string {
  if (!values || values.length < 2) return "→";
  
  const firstHalf = values.slice(0, Math.floor(values.length / 2));
  const secondHalf = values.slice(Math.floor(values.length / 2));
  
  const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
  const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
  
  const percentChange = ((avgSecond - avgFirst) / avgFirst) * 100;
  
  if (percentChange > 10) return "↑↑";
  if (percentChange > 0) return "↑";
  if (percentChange < -10) return "↓↓";
  if (percentChange < 0) return "↓";
  return "→";
}

/**
 * Generates mock historical data for each product
 * @param currentValue Current value to base the history on
 * @param growth Growth percentage string
 * @returns Array of 6 historical values
 */
function generateHistoricalData(currentValue: number, growth: string): number[] {
  const growthNum = parseFloat(growth.replace(/[+%]/g, ''));
  const isGrowing = growth.startsWith('+');
  
  const history: number[] = [];
  let value = currentValue;
  
  // Generate 6 data points (representing last 6 months)
  for (let i = 5; i >= 0; i--) {
    const variance = (Math.random() - 0.5) * 0.2; // ±10% random variance
    const trendFactor = isGrowing ? 
      (1 - (i * growthNum / 100 / 6)) : // Growing trend
      (1 + (i * Math.abs(growthNum) / 100 / 6)); // Declining trend
    
    history.push(Math.round(value * trendFactor * (1 + variance)));
  }
  
  return history;
}

/**
 * Inserts Amazon sales data into Excel worksheet with sparkline-like visualizations
 * Uses a dedicated "Sales Data" worksheet to avoid conflicts with Dashboard
 */
export async function insertAmazonDataToExcel() {
  try {
    await Excel.run(async (context) => {
      console.log("Starting data refresh on Sales Data worksheet with sparklines...");
      
      // Get or create the Sales Data worksheet
      let dataSheet: Excel.Worksheet;
      const worksheets = context.workbook.worksheets;
      worksheets.load("items/name");
      await context.sync();
      
      // Check if Sales Data worksheet exists
      let dataSheetExists = false;
      worksheets.items.forEach(sheet => {
        if (sheet.name === "Sales Data") {
          dataSheetExists = true;
          dataSheet = sheet;
        }
      });
      
      if (dataSheetExists) {
        // Clear the existing worksheet content
        const clearRange = dataSheet!.getRange("A1:L100");
        clearRange.clear();
        await context.sync();
      } else {
        // Create new Sales Data worksheet
        dataSheet = worksheets.add("Sales Data");
        await context.sync();
      }
      
      // Delete existing tables to avoid conflicts
      const tables = dataSheet.tables;
      tables.load("items/name");
      await context.sync();
      
      // Remove any existing tables
      if (tables.items.length > 0) {
        tables.items.forEach(table => {
          table.delete();
        });
        await context.sync();
      }
      
      // Add headers with new Trend column
      const headers = [
        ["SKU", "ASIN", "Product Name", "Category", "Revenue", "Units", "Velocity", "Growth %", "Status", "Last Sale", "AOV", "Trend"]
      ];
      
      const headerRange = dataSheet.getRange("A1:L1");
      headerRange.values = headers;
      headerRange.format.fill.color = "#4472C4";
      headerRange.format.font.color = "white";
      headerRange.format.font.bold = true;
      
      // Validate and prepare data with sparklines
      const dataValues = mockSalesData.products.map((item, index) => {
        // Generate historical data based on current units and growth
        const historicalUnits = generateHistoricalData(
          typeof item.Units === 'number' ? item.Units : 100,
          item.Growth
        );
        
        // Create sparkline visualization
        const sparkline = createInlineSparkline(historicalUnits);
        const trend = createTrendIndicator(historicalUnits);
        
        const row = [
          item.SKU || "",
          item.ASIN || "",
          item.ProductName || "",
          item.Category || "",
          item.Revenue || "$0",
          typeof item.Units === 'number' ? item.Units : 0,
          item.Velocity || "0",
          item.Growth || "0%",
          item.Status || "",
          item.LastSale || "",
          item.AOV || "$0",
          `${sparkline} ${trend}` // Sparkline visualization in last column
        ];
        
        // Validate row has exactly 12 columns
        if (row.length !== 12) {
          console.error(`Data row ${index} has ${row.length} columns instead of 12:`, row);
          throw new Error(`Data validation failed: Row ${index} has incorrect number of columns`);
        }
        
        return row;
      });
      
      console.log(`Setting ${dataValues.length} rows of data with sparklines...`);
      
      // Set data with error handling
      try {
        const dataRange = dataSheet.getRange(`A2:L${dataValues.length + 1}`);
        dataRange.values = dataValues;
        console.log("✓ Data with sparklines successfully set to range");
      } catch (rangeError) {
        console.error("Failed to set data range:", rangeError);
        throw new Error(`Data range error: ${rangeError.message}`);
      }
      
      // Format the Trend column for better visualization
      const trendColumnRange = dataSheet.getRange(`L2:L${dataValues.length + 1}`);
      trendColumnRange.format.font.size = 14; // Larger font for sparkline visibility
      trendColumnRange.format.horizontalAlignment = "Center";
      trendColumnRange.format.verticalAlignment = "Center";
      
      // Apply conditional formatting to Growth column for color coding
      for (let i = 0; i < dataValues.length; i++) {
        const growthCell = dataSheet.getRange(`H${2 + i}`);
        const statusCell = dataSheet.getRange(`I${2 + i}`);
        const trendCell = dataSheet.getRange(`L${2 + i}`);
        
        const growthValue = dataValues[i][7];
        const growthStr = String(growthValue);
        
        if (growthStr.startsWith('+')) {
          growthCell.format.font.color = "#28A745";
          statusCell.format.font.color = "#28A745";
          trendCell.format.font.color = "#28A745";
        } else if (growthStr.startsWith('-')) {
          growthCell.format.font.color = "#DC3545";
          statusCell.format.font.color = "#DC3545";
          trendCell.format.font.color = "#DC3545";
        }
      }
      
      // Create table with error handling
      try {
        const tableRange = dataSheet.getRange(`A1:L${dataValues.length + 1}`);
        const table = dataSheet.tables.add(tableRange, true);
        table.name = "SalesDataTable";
        table.style = "TableStyleMedium2";
        console.log("✓ Table created successfully with sparklines");
      } catch (tableError) {
        console.error("Failed to create table:", tableError);
        // Continue without table if creation fails
        console.log("Continuing without table formatting...");
      }
      
      // Auto-fit columns (except the Trend column which should be wider)
      try {
        dataSheet.getRange("A:K").format.autofitColumns();
        dataSheet.getRange("L:L").format.columnWidth = 100; // Wider column for sparklines
        console.log("✓ Columns auto-fitted, sparkline column widened");
      } catch (autofitError) {
        console.error("Failed to autofit columns:", autofitError);
        // Continue without autofit if it fails
      }
      
      // Add a note about the sparkline column
      const noteRange = dataSheet.getRange("L1");
      noteRange.format.wrapText = false;
      
      // Activate the data sheet to show the refreshed data
      dataSheet.activate();
      
      await context.sync();
      console.log("✓ Data refresh with sparklines completed successfully!");
    });
  } catch (error) {
    console.error("Error inserting data to Excel:", error);
    throw error;
  }
}

/**
 * Creates a comprehensive Sales Performance Dashboard in Excel
 * Exactly matching the provided image design
 */
export async function createSalesPerformanceDashboard() {
  try {
    await Excel.run(async (context) => {
      console.log("Starting dashboard creation...");
      
      // Get the active worksheet
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      
      // Clear the entire worksheet first
      const clearRange = sheet.getRange("A1:Z100");
      clearRange.clear();
      await context.sync();
      
      // ... rest of your code
    });
  } catch (error) {
    console.error("Error in createSalesPerformanceDashboard:", error);
    throw error;
  }
}

// Helper function to set borders on all edges
function setBorders(range: Excel.Range, color: string, weight: string) {
  const border1 = range.format.borders.getItem("EdgeTop" as any);
  border1.style = "Continuous";
  border1.color = color;
  border1.weight = weight as any;
  
  const border2 = range.format.borders.getItem("EdgeBottom" as any);
  border2.style = "Continuous";
  border2.color = color;
  border2.weight = weight as any;
  
  const border3 = range.format.borders.getItem("EdgeLeft" as any);
  border3.style = "Continuous";
  border3.color = color;
  border3.weight = weight as any;
  
  const border4 = range.format.borders.getItem("EdgeRight" as any);
  border4.style = "Continuous";
  border4.color = color;
  border4.weight = weight as any;
}